import React, { Dispatch, SetStateAction, useState, useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import Dropzone from 'react-dropzone';
import { getBackgroundTheme, getBorders } from '../../../style';
import ConversationTextArea from './ConversationTextArea';
import { getBrowserDetails } from '../../../utils/helpers';
import { PhotoConfig } from '../../../config/Media';
import MasterConfig, { enabledPhotoMessage } from '../../../config/Master';
import { commonIcons } from '../../../atoms/icons/materialIcons';
import MessageBubble from '../../../atoms/mail/MessageBubble';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../atoms/buttons/Button';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { Picker } from 'emoji-mart';
import { RootState } from '../../../store';
import { handlePostTypingStatus, triggerPostMessageProcess, uploadPhotoMessage } from '../../../actions/mailActions';
import UnsupportedFileTypeErrorModal from '../../modals/UnsupportedFileTypeErrorModal';
import { isEmpty } from 'lodash';
import breakpoints from '../../../theme/material/breakpoints';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { IThread } from 'actions/types/mailActions';
import colors from 'theme/colors';

interface IConversationActions {
  uploadInProgress: boolean;
  scrollToBottom: () => void;
  username: string;
  otherAccountHashId: string;
  setMessagesCollection: Dispatch<SetStateAction<any>>;
  thread: IThread;
}

const { isIos, isMobile } = getBrowserDetails();

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const StyledConversationMobileTextArea = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  padding: 10,
  position: 'relative',
  bottom: 0,
  overflow: 'visible',

  [breakpoints.down(768.05)]: {
    position: 'relative',
    width: '100%',
    backgroundColor: 'transparent',
    marginTop: 1,
    maxHeight: 100,
    padding: 0,
    paddingBottom: isIos ? 20 : 5,
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const ConversationInput = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  padding: '20px 20px 0 20px',
  position: 'relative',
  bottom: 0,
  overflow: 'visible',

  [breakpoints.down(768.05)]: {
    position: 'fixed',
    width: `calc(100vw - 20px)`,
    backgroundColor: getBackgroundTheme().block,
    marginTop: 1,
    maxHeight: 100,
    padding: 10,
    paddingBottom: isIos ? 20 : 5,
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const MobileConversationWrapper = styled('div')(() => ({
  [breakpoints.down(768.05)]: {
    position: 'fixed',
    width: '100vw',
    backgroundColor: getBackgroundTheme().block,
    bottom: 0,
    left: 0,
    zIndex: 10,
    padding: '0 10px',
    boxSizing: 'border-box',
  },
}));

const ConversationButtons = styled('div')(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    display: 'flex',
    flexDirection: 'row',
    padding: '10px 0',
    borderTop: getBorders().DEFAULT,
    position: 'relative',
    bottom: 0,
    overflow: 'visible',

    [breakpoints.down(768.05)]: {
      position: 'relative',
      width: '100%',
      backgroundColor: 'transparent',
      marginTop: 1,
      maxHeight: 100,
      marginBottom: 60,
    },
  }),
  () => ({
    [breakpoints.down(768.05)]: {
      paddingBottom: 5,
      marginBottom: 0,
    },
  })
);

const StyledDropzone = styled(Dropzone)({
  cursor: 'pointer',
  padding: getBrowserDetails().isMobile ? '1px' : '0 6px 0 0',
  height: 'auto',
  width: 40,
  float: 'left',
  marginTop: getBrowserDetails().isMobile ? '-2px' : 0,
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const TypingIndicator = styled('div')(() => ({
  fontStyle: 'italic',
  fontSize: 12,
  position: 'absolute',
  top: -20,
  paddingLeft: 10,
  height: 13,
  marginTop: -20,
  marginLeft: getBrowserDetails().isMobile ? 33 : 43,
}));

const emojiCustomStyle = { color: colors.dark.blue.secondary };

const ConversationActions: React.FC<IConversationActions> = ({
  username,
  uploadInProgress,
  scrollToBottom,
  setMessagesCollection,
  otherAccountHashId,
  thread,
}) => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showMandatoryWall } = useMandatoryWall();

  const isOtherAccountTyping = useSelector((state: RootState) => state?.mail?.isOtherAccountTyping ?? false);
  const hasPendingMessageToSend = useSelector((state: RootState) => !isEmpty(state?.mail?.messagePendingUpdate));
  const authHashId = useSelector((state: RootState) => state?.profile?.hash_id ?? '');

  const bigIpad = getBrowserDetails().isIpad && screen.height > 1024;
  const defaultPickerBottomOffset = 130;
  const [pickerBottomOffset, setPickerBottomOffset] = useState(defaultPickerBottomOffset);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [typingIndicator, setTypingIndicator] = useState<boolean>(false);
  const [textAreaHeight, setTextAreaHeight] = useState(0);
  const [showUnsupportedFileModal, setShowUnsupportedFileModal] = useState(false);
  const dropzoneRef = useRef(null);

  const shouldDisabledSendMessage = useMemo(() => {
    return hasPendingMessageToSend || uploadInProgress;
  }, [hasPendingMessageToSend, uploadInProgress]);

  const calcInputContainerHeight = () => {
    const convContainer = document.getElementsByClassName('conversation-input-container');

    if (typeof convContainer[0] === 'undefined') {
      return defaultPickerBottomOffset;
    }

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    return convContainer[0].offsetHeight;
  };

  useEffect(() => {
    if (!uploadInProgress) {
      setTimeout(() => {
        scrollToBottom();
      }, 2000);
    }
  }, [uploadInProgress]);

  useEffect(() => {
    if (dropzoneRef?.current) {
      showMandatoryWall(
        () => {
          dropzoneRef?.current?.open();
        },
        () => undefined,
        'photoMessage',
        false,
        ['profileWall']
      );
    }
  }, [dropzoneRef]);

  useEffect(() => {
    const textAreaStyled = document.getElementById('textAreaStyled');

    textAreaStyled && setTextAreaHeight(textAreaStyled.clientHeight);
  }, []);

  const hidePickers = () => {
    setShowEmojiPicker(false);
  };

  const showHidePicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setPickerBottomOffset(calcInputContainerHeight());
  };

  const handleAddEmoji = e => {
    const emoji = e.native;
    const myMessage = `${message}${emoji}`;

    setMessage(myMessage);

    if (typingIndicator === null && message.trim() !== '') {
      if (params.hashId) {
        dispatch(handlePostTypingStatus(otherAccountHashId, thread.uid));

        setTypingIndicator(true);
        setTimeout(() => {
          setTypingIndicator(false);
        }, 6000);
      }
    }
  };

  const handleUploadPhotoMessage = async (formData, hashID) => {
    const fileName = formData?.name;

    if (!fileName) {
      return undefined;
    }

    const extension = fileName.split('.');
    const resPhoto = await dispatch(uploadPhotoMessage(formData, hashID, false, thread.uid));

    if (resPhoto) {
      dispatch(
        triggerPostMessageProcess(
          resPhoto.uuid ?? resPhoto.message,
          'photo',
          null,
          extension[extension.length - 1],
          params,
          navigate,
          location,
          resPhoto.uuid
        )
      );
    }

    return resPhoto;
  };

  const onDropPhoto = async (imageFile, rejectFiles) => {
    showMandatoryWall(
      async () => {
        if (rejectFiles?.length > 0) {
          setShowUnsupportedFileModal(true);
          return;
        }

        hidePickers();
        // create temp data for processing
        const date = moment().format('YY–MM-DD HH:mm:ss');
        const processData = {
          created_at: {
            date,
          },
          hash_id: authHashId,
          id: null,
          type: 'photo',
          is_deleted: false,
          is_visible: true,
          message: '',
          isBlurred: false,
          isUploading: true,
        };

        setMessagesCollection(prevState => {
          return [...prevState, processData];
        });

        const resImage = await handleUploadPhotoMessage(imageFile[0], otherAccountHashId);

        if (resImage === undefined) return;
      },
      () => undefined,
      'photoMessage',
      false,
      ['profileWall']
    );
  };

  const handleSubmitPhoto = () => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    onDropPhoto(document.getElementById('upload-qa-message-photo').files);
  };

  const openFileManager = () => {
    handleCloseUnsupportedFileTypeModal();

    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const handleCloseUnsupportedFileTypeModal = () => {
    setShowUnsupportedFileModal(false);
  };

  const memoizedSelectEmoji = useMemo(
    () => (
      <div
        data-test-id="select-emoji-container"
        style={{
          display: showEmojiPicker ? 'block' : 'none',
          position: isMobile && !bigIpad ? 'fixed' : 'initial',
          height: '100%',
          width: isMobile && !bigIpad ? '100%' : 'auto',
          bottom: pickerBottomOffset,
          left: 0,
          zIndex: 100,
        }}
      >
        <Picker
          style={{
            minWidth: '100%',
            position: isMobile && !bigIpad ? 'fixed' : 'absolute',
            bottom: pickerBottomOffset,
            left: 0,
            display: showEmojiPicker ? 'block' : 'none',
            backgroundColor: getBackgroundTheme().block,
          }}
          onSelect={handleAddEmoji}
          title="Pick your emoji…"
          emoji="point_up"
        />
      </div>
    ),
    [showEmojiPicker, pickerBottomOffset, bigIpad, handleAddEmoji]
  );

  const memoizedEmojiBtn = useMemo(() => {
    const emojiIcon = shouldDisabledSendMessage
      ? commonIcons.emoticon.inactive(emojiCustomStyle)
      : commonIcons.emoticon.default(emojiCustomStyle);

    return (
      <Button
        customStyle={{ padding: '0 6px', height: 35, width: 45, float: 'left' }}
        onClick={showHidePicker}
        buttonType="chromeless"
        data-test-id="emoji-picker-button"
        disabled={shouldDisabledSendMessage}
      >
        {showEmojiPicker ? commonIcons.emoticon.keyboard(emojiCustomStyle) : emojiIcon}
      </Button>
    );
  }, [showEmojiPicker, shouldDisabledSendMessage]);

  const memoizedTypingIndicator = useMemo(
    () =>
      isOtherAccountTyping ? (
        <TypingIndicator>
          {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
          <MessageBubble
            key={Math.floor(Math.random() * 999)}
            isRead
            id={Math.floor(Math.random() * 999)}
            type="indicator"
          />
        </TypingIndicator>
      ) : (
        ' '
      ),
    [isOtherAccountTyping]
  );

  const memoizedDropZone = useMemo(
    () => (
      <StyledDropzone
        innerRef={dropzoneRef}
        accept={PhotoConfig.allowedMimeType}
        multiple={false}
        onDrop={onDropPhoto}
        maxSize={MasterConfig.MAX_PHOTO_FILE_SIZE}
        disabled={shouldDisabledSendMessage || !enabledPhotoMessage}
        disableClick={shouldDisabledSendMessage || !enabledPhotoMessage}
      >
        {shouldDisabledSendMessage
          ? commonIcons.photo.inactive(emojiCustomStyle)
          : commonIcons.photo.default(emojiCustomStyle)}
      </StyledDropzone>
    ),
    [shouldDisabledSendMessage, enabledPhotoMessage]
  );

  return (
    <>
      <div data-test-id="hidden-upload-form" style={{ display: 'none' }}>
        <form>
          <input type="file" name="file" id="upload-qa-message-photo" data-test-id="upload-qa-message-photo" />

          <button
            type="button"
            id="submit-qa-message-photo"
            data-test-id="submit-qa-message-photo"
            onClick={handleSubmitPhoto}
          >
            Submit
          </button>
        </form>
      </div>

      {!isMobile ? (
        <ConversationInput className="conversation-input-container">
          {memoizedTypingIndicator}
          {memoizedEmojiBtn}
          <div style={{ height: 38 }}>{memoizedDropZone}</div>
          {memoizedSelectEmoji}
          <ConversationTextArea
            hidePickers={hidePickers}
            disableSend={shouldDisabledSendMessage}
            username={username}
            scrollToBottom={scrollToBottom}
            message={message}
            setMessage={setMessage}
            typingIndicator={typingIndicator}
            setTypingIndicator={setTypingIndicator}
            otherAccountHashId={otherAccountHashId}
            thread={thread}
          />
        </ConversationInput>
      ) : (
        <MobileConversationWrapper>
          {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
          <ConversationButtons className="conversation-button-container" textAreaHeight={textAreaHeight}>
            {memoizedTypingIndicator}
            <div style={{ height: 38, margin: '0 auto' }}>
              {memoizedEmojiBtn}
              {memoizedDropZone}
            </div>
            {memoizedSelectEmoji}
          </ConversationButtons>

          <StyledConversationMobileTextArea>
            <ConversationTextArea
              hidePickers={hidePickers}
              disableSend={shouldDisabledSendMessage}
              username={username}
              scrollToBottom={scrollToBottom}
              message={message}
              setMessage={setMessage}
              typingIndicator={typingIndicator}
              setTypingIndicator={setTypingIndicator}
              otherAccountHashId={otherAccountHashId}
              thread={thread}
            />
          </StyledConversationMobileTextArea>
        </MobileConversationWrapper>
      )}
      <UnsupportedFileTypeErrorModal
        isOpen={showUnsupportedFileModal}
        onClose={handleCloseUnsupportedFileTypeModal}
        onUploadAnother={openFileManager}
      />
    </>
  );
};

export default React.memo(ConversationActions);
